import { call, fork, put, takeLatest, select, delay, takeEvery } from 'redux-saga/effects';
import { parse, stringify } from 'query-string';
import { omit } from 'lodash-es';
import { push as Push, RouterState } from 'redux-first-history';

import {
    loadSummary,
    loadTodaySummary,
    loadLinkTracking,
    exportLinks,
    exportLinkContacts,
    checkTaskStatus,
    getLinkContacts,
    getBouncedSummary,
} from '@api/services/ElasticApi';
import { ExportStatus } from '@dev/ee-api/ee-api-models';
import {
    LoadSummarySuccess,
    LoadSummaryError,
    LoadTodaySummarySuccess,
    LoadTodaySummaryError,
    LoadLinkTrackingListSuccess,
    LoadLinkTrackingListError,
    ExportLinksSuccess,
    ExportLinksError,
    ExportLinksStart,
    ExportLinkContactsSuccess,
    ExportLinkContactsError,
    ExportLinkContactsStart,
    LoadLinkContactsSuccess,
    LoadLinkContactsError,
    LoadBouncedSummarySuccess,
} from '@api/store/statistics/actions';
import {
    LoadSummaryFetchStartAction,
    LoadTodaySummaryFetchStartAction,
    LoadLinkTrackingListFetchStartAction,
    FindLinkTrackingAction,
    ExportLinksAction,
    ExportLinkContactsAction,
    LoadLinkContactsStartAction,
    BouncedSummaryTypes,
    LoadBouncedSummaryAction,
} from '@api/store/statistics/types';

import { ShowToast, HideToast } from '@api/store/toast/actions';
import { ApplicationState, ApplicationState as State } from '@api/store';
import { errorToast } from './commonGenerators/errorToast';

const LINK_TRACKING_SEARCH = '@@statistics/LINKTRACKING_SEARCH';
const MAX_PER_PAGE = 10;
const getLinkTrackingActivePage = (state: State): number => state.statistics.linkTrackingActivePage;
const getLinkContactsActivePage = (state: State): number => state.statistics.linkContactsActivePage;
const getLinkTrackingPerPage = (state: State): number => state.statistics.linkTrackingPerPage;
const getLinkContactsPerPage = (state: State): number => state.statistics.linkContactsPerPage;

function* handleURLQuery(payload, type: string, activePage: number, perPage: number): any {
    const { location }: RouterState = yield select((store: ApplicationState) => store.router);
    let queryParams = parse(location.search);

    if (type === LINK_TRACKING_SEARCH) {
        if (payload && payload.linkSearch) {
            queryParams.search = payload.linkSearch;
        } else {
            queryParams = omit(queryParams, 'search');
        }
    }

    activePage < 2 ? (queryParams = omit(queryParams, 'page')) : (queryParams.page = activePage.toString());

    const maxElements = parseInt(queryParams.perPage as string);

    if (maxElements !== perPage && perPage !== MAX_PER_PAGE) {
        queryParams.perPage = perPage.toString();
    }

    if (perPage === MAX_PER_PAGE) {
        queryParams = omit(queryParams, 'perPage');
    }

    yield put(Push({ search: stringify(queryParams) }));
}

const getOffsetAndLimit = (activePage: number, perPage?: number): { offset: number; limit: number } => {
    const itemsOnPage = perPage || MAX_PER_PAGE;
    const offset = itemsOnPage * activePage - itemsOnPage;
    const limit = perPage;

    return { offset, limit };
};

function* fetchSummary(action: LoadSummaryFetchStartAction): any {
    try {
        const { payload } = action;
        const params = {
            from: payload.from,
            to: payload.to,
            channelName: payload.channelName,
            interval: payload.interval,
            transactionID: payload.transactionID,
        };
        const summary = yield call(loadSummary, params);

        yield put(LoadSummarySuccess(summary?.data));
    } catch {
        yield put(LoadSummaryError());
    }
}

function* fetchTodaySummary(action: LoadTodaySummaryFetchStartAction): any {
    try {
        const { payload } = action;

        const params = {
            from: payload.from,
            to: payload.to,
            channelName: payload.channelName,
            interval: payload.interval,
            transactionID: payload.transactionID,
        };
        const todaySummary = yield call(loadTodaySummary, params);
        yield put(LoadTodaySummarySuccess(todaySummary?.data));
    } catch (e) {
        yield put(LoadTodaySummaryError());
        yield call(errorToast, e);
    }
}

function* fetchLinkTrackingList(action: LoadLinkTrackingListFetchStartAction | FindLinkTrackingAction): any {
    try {
        const { payload, type } = action;
        const { activePage = yield select(getLinkTrackingActivePage), perPage = yield select(getLinkTrackingPerPage) } = payload;

        if (type === LINK_TRACKING_SEARCH) {
            yield delay(500);
        }

        yield handleURLQuery(payload, type, activePage, perPage);

        const { offset, limit } = getOffsetAndLimit(activePage, perPage);
        const linkTracking = yield call(loadLinkTracking, {
            limit,
            offset,
            channelName: payload.channelName,
            linkSearch: payload.linkSearch,
            from: payload.from,
            to: payload.to,
        });

        const totalLinkCount = parseInt(linkTracking.headers['x-total-count']) || 0;
        yield put(LoadLinkTrackingListSuccess(linkTracking?.data, totalLinkCount, activePage, perPage));
    } catch {
        yield put(LoadLinkTrackingListError());
    }
}

function* exportLink(action: ExportLinksAction): any {
    const { payload } = action;
    let checkExportStatus = true;

    yield put(ExportLinksStart());

    try {
        const resp = yield call(exportLinks, payload);

        while (checkExportStatus) {
            yield delay(5000);
            const statusResp = yield call(checkTaskStatus, resp.data.PublicExportID);
            const status: ExportStatus = statusResp.data;

            if (status === ExportStatus.Ready) {
                checkExportStatus = false;
            }

            if (status === ExportStatus.Error || status === ExportStatus.Expired) {
                throw new Error(statusResp);
            }
        }

        yield put(Push('/exports'));

        yield put(ExportLinksSuccess());
        yield put(
            ShowToast({
                severity: 'info',
                message: `Links exported`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    } catch (e) {
        yield put(ExportLinksError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e.response.data.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* fetchLinkContactsList(action: LoadLinkContactsStartAction): any {
    try {
        const { payload, type } = action;
        const { linkContactsActivePage = yield select(getLinkContactsActivePage), perPage = yield select(getLinkContactsPerPage) } =
            payload;

        yield handleURLQuery(payload, type, linkContactsActivePage, perPage);

        const { offset, limit } = getOffsetAndLimit(linkContactsActivePage, perPage);
        const linkContactsList = yield call(getLinkContacts, {
            limit,
            offset,
            channelName: payload.channelName,
            link: payload.link,
            from: payload.from,
            to: payload.to,
        });

        const totalLinkContactsCount = parseInt(linkContactsList.headers['x-total-count']) || 0;
        yield put(LoadLinkContactsSuccess(linkContactsList?.data, totalLinkContactsCount, linkContactsActivePage, perPage));
    } catch {
        yield put(LoadLinkContactsError());
    }
}

function* exportLinkContact(action: ExportLinkContactsAction): any {
    const { payload } = action;
    let checkExportStatus = true;

    yield put(ExportLinkContactsStart());

    try {
        const resp = yield call(exportLinkContacts, payload);

        while (checkExportStatus) {
            yield delay(5000);
            const statusResp = yield call(checkTaskStatus, resp.data.PublicExportID);
            const status: ExportStatus = statusResp.data;

            if (status === ExportStatus.Ready) {
                checkExportStatus = false;
            }

            if (status === ExportStatus.Error || status === ExportStatus.Expired) {
                throw new Error(statusResp);
            }
        }

        yield put(ExportLinkContactsSuccess());
        yield put(
            ShowToast({
                severity: 'info',
                message: `Contacts exported`,
                duration: 6000,
                onClose: HideToast,
            })
        );
        yield put(Push('/exports'));
    } catch (e) {
        yield put(ExportLinkContactsError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e.response.data.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* fetchBouncedSummary(action: LoadBouncedSummaryAction): any {
    const { payload } = action;

    try {
        const result = yield call(getBouncedSummary, payload);
        yield put(LoadBouncedSummarySuccess(result?.data));
    } catch (e) {
        yield put(LoadSummaryError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e.response.data.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchFetchSummary(): any {
    yield takeLatest('@@statistics/LOADSUMMARY_FETCH_START', fetchSummary);
}
function* watchFetchTodaySummary(): any {
    yield takeLatest('@@statistics/LOADTODAYSUMMARY_FETCH_START', fetchTodaySummary);
}
function* watchFetchLinkTrackingList(): any {
    yield takeLatest('@@statistics/LOADLINKTRACKING_START', fetchLinkTrackingList);
}
function* watchFindLinks(): any {
    yield takeLatest(LINK_TRACKING_SEARCH, fetchLinkTrackingList);
}
function* watchExportLinks(): any {
    yield takeLatest('@@statistics/EXPORT_LINKS', exportLink);
}
function* watchFetchLinkContactsList(): any {
    yield takeLatest('@@statistics/LINKCONTACTS_START', fetchLinkContactsList);
}
function* watchExportLinkContacts(): any {
    yield takeLatest('@@statistics/EXPORT_LINKCONTACTS', exportLinkContact);
}
function* fetchBouncedSummaryWatcher(): any {
    yield takeEvery(BouncedSummaryTypes.LOAD, fetchBouncedSummary);
}

const StatisticsSagas = [
    fork(watchFetchSummary),
    fork(watchFetchTodaySummary),
    fork(watchFetchLinkTrackingList),
    fork(watchFindLinks),
    fork(watchExportLinks),
    fork(watchFetchLinkContactsList),
    fork(watchExportLinkContacts),
    fork(fetchBouncedSummaryWatcher),
];

export default StatisticsSagas;
